// apps/accounts/src/pages/Vendors.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Eye, Pencil, Trash2, X, Upload, User, Building2, Banknote, CreditCard, 
  MapPin, Phone, Mail, IdCard, Users, Calendar, TrendingUp, DollarSign,
  Clock, CheckCircle, AlertCircle, Loader2
} from 'lucide-react';
import pb from '../lib/pocketbaseClient';
import { format } from 'date-fns';
import { cn } from '../lib/utils';

export default function Vendors() {
  const [vendors, setVendors] = useState([]);
  const [labours, setLabours] = useState([]);
  const [sites, setSites] = useState([]);
  const [payments, setPayments] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('All');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'meson',
    phone: '',
    email: '',
    address: '',
    aadhar: '',
    accountName: '',
    accountNumber: '',
    bankName: '',
    ifscCode: '',
    bankDetails: '',
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [aadharPhotoFile, setAadharPhotoFile] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Detail modal
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const isMounted = useRef(true);

  const vendorTypes = [
    'meson', 'foreman', 'painter', 'electrician', 'plumber',
    'tiles_worker', 'gardener', 'carpenter', 'supply_labour', 'other'
  ];

  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      const [vendorsRes, laboursRes, sitesRes, paymentsRes, attendanceRes] = await Promise.all([
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('labour').getFullList({ expand: 'site' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
        pb.collection('vendor_payments').getFullList({ sort: '-paymentDate' }),
        pb.collection('labour_attendance').getFullList({ sort: '-date' }),
      ]);
      if (isMounted.current) {
        setVendors(vendorsRes);
        setLabours(laboursRes);
        setSites(sitesRes);
        setPayments(paymentsRes);
        setAttendance(attendanceRes);
      }
    } catch (err) {
      console.error(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  // ─── Form Handlers ──────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, setter) => {
    setter(e.target.files[0] || null);
  };

  const prepareFormData = (data) => {
    const fd = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        fd.append(key, data[key]);
      }
    });
    if (photoFile) fd.append('photo', photoFile);
    if (aadharPhotoFile) fd.append('aadharPhoto', aadharPhotoFile);
    return fd;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const data = { ...formData };
      Object.keys(data).forEach(key => {
        if (data[key] === '') data[key] = null;
      });

      if (editingVendor) {
        const fd = prepareFormData(data);
        await pb.collection('vendors').update(editingVendor.id, fd);
      } else {
        const fd = prepareFormData(data);
        await pb.collection('vendors').create(fd);
      }
      handleCloseModal();
      fetchData();
    } catch (err) {
      console.error('Save error:', err);
      let msg = err.message || err.data?.message || 'Failed to save vendor';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data)
          .map(([k, v]) => `${k}: ${v.message}`)
          .join(', ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      name: vendor.name || '',
      type: vendor.type || 'meson',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
      aadhar: vendor.aadhar || '',
      accountName: vendor.accountName || '',
      accountNumber: vendor.accountNumber || '',
      bankName: vendor.bankName || '',
      ifscCode: vendor.ifscCode || '',
      bankDetails: vendor.bankDetails || '',
    });
    setPhotoFile(null);
    setAadharPhotoFile(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;
    try {
      await pb.collection('vendors').delete(id);
      fetchData();
    } catch (err) {
      alert('Failed to delete vendor: ' + err.message);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVendor(null);
    setFormData({
      name: '',
      type: 'meson',
      phone: '',
      email: '',
      address: '',
      aadhar: '',
      accountName: '',
      accountNumber: '',
      bankName: '',
      ifscCode: '',
      bankDetails: '',
    });
    setPhotoFile(null);
    setAadharPhotoFile(null);
    setFormError('');
    setFormLoading(false);
  };

  // ─── Detail Modal Handlers ──────────────────────────────────
  const openDetailModal = (vendor) => {
    setSelectedVendor(vendor);
    setFormData({
      name: vendor.name || '',
      type: vendor.type || 'meson',
      phone: vendor.phone || '',
      email: vendor.email || '',
      address: vendor.address || '',
      aadhar: vendor.aadhar || '',
      accountName: vendor.accountName || '',
      accountNumber: vendor.accountNumber || '',
      bankName: vendor.bankName || '',
      ifscCode: vendor.ifscCode || '',
      bankDetails: vendor.bankDetails || '',
    });
    setPhotoFile(null);
    setAadharPhotoFile(null);
    setEditMode(false);
    setFormError('');
    setActiveTab('overview');
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedVendor(null);
    setEditMode(false);
    setPhotoFile(null);
    setAadharPhotoFile(null);
    setFormError('');
  };

  const handleEditFromDetail = () => {
    setEditMode(true);
  };

  const handleSaveFromDetail = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');
    try {
      const data = { ...formData };
      Object.keys(data).forEach(key => {
        if (data[key] === '') data[key] = null;
      });
      const fd = prepareFormData(data);
      await pb.collection('vendors').update(selectedVendor.id, fd);
      closeDetailModal();
      fetchData();
    } catch (err) {
      console.error(err);
      let msg = err.message || 'Failed to update vendor';
      if (err.data?.data) {
        const fields = Object.entries(err.data.data)
          .map(([k, v]) => `${k}: ${v.message}`)
          .join(', ');
        msg += ` (${fields})`;
      }
      setFormError(msg);
      setFormLoading(false);
    }
  };

  // ─── Vendor Stats ──────────────────────────────────────────
  const getVendorStats = (vendorId) => {
    const vendorLabours = labours.filter(l => l.vendor === vendorId);
    const total = vendorLabours.length;
    const skilled = vendorLabours.filter(l => l.skill === 'skilled').length;
    const unskilled = total - skilled;
    const siteMap = {};
    vendorLabours.forEach(l => {
      const siteId = l.site;
      if (!siteMap[siteId]) siteMap[siteId] = { skilled: 0, unskilled: 0, total: 0 };
      siteMap[siteId].total++;
      if (l.skill === 'skilled') siteMap[siteId].skilled++;
      else siteMap[siteId].unskilled++;
    });
    const siteBreakdown = Object.keys(siteMap).map(siteId => {
      const site = sites.find(s => s.id === siteId);
      return {
        siteName: site ? site.name : 'Unknown',
        ...siteMap[siteId],
      };
    });
    return { total, skilled, unskilled, siteBreakdown, labourIds: vendorLabours.map(l => l.id) };
  };

  // ─── Attendance Stats ──────────────────────────────────────
  const getVendorAttendance = (vendorId) => {
    const vendorLabours = labours.filter(l => l.vendor === vendorId);
    const labourIds = vendorLabours.map(l => l.id);
    const vendorAttendance = attendance.filter(a => labourIds.includes(a.labour));
    
    // Weekly stats (last 7 days)
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - 7);
    
    const weekAttendance = vendorAttendance.filter(a => {
      const date = new Date(a.date);
      return date >= weekStart && date <= today;
    });
    
    // Daily average for the week
    const daysInWeek = 7;
    const dailyCounts = {};
    for (let i = 0; i < daysInWeek; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      const key = d.toDateString();
      dailyCounts[key] = 0;
    }
    weekAttendance.forEach(a => {
      const key = new Date(a.date).toDateString();
      if (dailyCounts[key] !== undefined) dailyCounts[key]++;
    });
    
    const avgDaily = Object.values(dailyCounts).reduce((sum, val) => sum + val, 0) / daysInWeek;
    const totalPresent = weekAttendance.length;
    const totalPossible = vendorLabours.length * daysInWeek;
    const attendanceRate = totalPossible > 0 ? (totalPresent / totalPossible) * 100 : 0;

    return {
      weekAttendance,
      avgDaily: Math.round(avgDaily * 10) / 10,
      totalPresent,
      attendanceRate: Math.round(attendanceRate * 10) / 10,
      dailyCounts,
      labourCount: vendorLabours.length,
    };
  };

  // ─── Payment Stats ──────────────────────────────────────────
  const getVendorPayments = (vendorId) => {
    const vendorPayments = payments.filter(p => p.vendor === vendorId);
    const totalPaid = vendorPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
    const lastPayment = vendorPayments.length > 0 ? vendorPayments[0] : null;
    const paymentCount = vendorPayments.length;
    return { totalPaid, lastPayment, paymentCount, payments: vendorPayments };
  };

  // ─── Render ──────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading vendors...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <span>👥 Vendors</span>
            <Badge variant="outline" className="ml-2">{vendors.length}</Badge>
          </h1>
          <p className="text-sm text-slate-500">Manage all vendors with labour counts per site</p>
        </div>
        <Button className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white shadow-md" onClick={() => { setEditingVendor(null); setShowModal(true); }}>
          + New Vendor
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex-1 min-w-[200px]">
          <Input placeholder="🔍 Search vendors..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400">
          <option value="All">All Types</option>
          {vendorTypes.map(type => (
            <option key={type} value={type}>{type.replace('_', ' ')}</option>
          ))}
        </select>
        <Button variant="outline" onClick={() => { setSearch(''); setFilterType('All'); }}>Clear</Button>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Type</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Phone</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Labour</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase">Paid</th>
                <th className="px-6 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {vendors.length === 0 ? (
                <tr><td colSpan="6" className="px-6 py-8 text-center text-slate-500">No vendors found.</td></tr>
              ) : (
                vendors.map(v => {
                  const stats = getVendorStats(v.id);
                  const payments = getVendorPayments(v.id);
                  return (
                    <tr key={v.id} className="hover:bg-slate-50/50 transition">
                      <td className="px-6 py-3">
                        <button
                          onClick={() => openDetailModal(v)}
                          className="font-medium text-blue-600 hover:underline hover:text-amber-600 flex items-center gap-2"
                        >
                          {v.photo && (
                            <img
                              src={pb.files.getUrl(v, v.photo)}
                              alt={v.name}
                              className="w-6 h-6 rounded-full object-cover border"
                            />
                          )}
                          {v.name}
                        </button>
                      </td>
                      <td className="px-6 py-3">{getTypeBadge(v.type)}</td>
                      <td className="px-6 py-3">{v.phone || '-'}</td>
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="bg-blue-50">{stats.total} total</Badge>
                          <Badge variant="success" className="bg-emerald-50">{stats.skilled} skilled</Badge>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        <span className="font-mono font-bold text-green-600">₹{payments.totalPaid.toLocaleString()}</span>
                        <span className="text-xs text-slate-400 ml-1">({payments.paymentCount} payments)</span>
                      </td>
                      <td className="px-6 py-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5 h-auto text-blue-600 hover:bg-blue-50"
                            onClick={() => openDetailModal(v)}
                            title="View Details"
                          >
                            <Eye size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5 h-auto text-amber-600 hover:bg-amber-50"
                            onClick={() => handleEdit(v)}
                            title="Edit Vendor"
                          >
                            <Pencil size={18} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-1.5 h-auto text-red-600 hover:bg-red-50"
                            onClick={() => handleDelete(v.id)}
                            title="Delete Vendor"
                          >
                            <Trash2 size={18} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* ─── Create/Edit Modal ─────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
              {editingVendor ? '✏️ Edit Vendor' : '➕ New Vendor'}
              <Badge variant="outline" className="ml-2">{editingVendor ? 'Update' : 'Create'}</Badge>
            </h2>
            {formError && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200 whitespace-pre-wrap">
                {formError}
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Name *</label>
                  <Input name="name" value={formData.name} onChange={handleChange} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                  <select name="type" value={formData.type} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                    {vendorTypes.map(type => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <Input name="phone" value={formData.phone} onChange={handleChange} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <Input name="email" type="email" value={formData.email} onChange={handleChange} />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <Input name="address" value={formData.address} onChange={handleChange} />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Aadhar Number</label>
                <Input name="aadhar" value={formData.aadhar} onChange={handleChange} />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Photo</label>
                  <div className="flex items-center gap-2">
                    <Input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setPhotoFile)} className="flex-1" />
                    {photoFile && <span className="text-xs text-green-600">✓ {photoFile.name}</span>}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Aadhar Card Photo</label>
                  <div className="flex items-center gap-2">
                    <Input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setAadharPhotoFile)} className="flex-1" />
                    {aadharPhotoFile && <span className="text-xs text-green-600">✓ {aadharPhotoFile.name}</span>}
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 shadow-sm">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Banknote size={16} className="text-blue-600" />
                  Bank Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Account Name</label>
                    <Input name="accountName" value={formData.accountName} onChange={handleChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Account Number</label>
                    <Input name="accountNumber" value={formData.accountNumber} onChange={handleChange} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Bank Name</label>
                    <Input name="bankName" value={formData.bankName} onChange={handleChange} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">IFSC Code</label>
                    <Input name="ifscCode" value={formData.ifscCode} onChange={handleChange} />
                  </div>
                </div>
                <div className="mt-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <Input name="bankDetails" value={formData.bankDetails} onChange={handleChange} placeholder="Additional bank info" />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <Button type="button" variant="outline" onClick={handleCloseModal}>Cancel</Button>
                <Button type="submit" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white shadow-md" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingVendor ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Detail Modal ──────────────────────────────────────── */}
      {detailModalOpen && selectedVendor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                {selectedVendor.photo ? (
                  <img
                    src={pb.files.getUrl(selectedVendor, selectedVendor.photo)}
                    alt={selectedVendor.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-amber-400"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-slate-200 flex items-center justify-center">
                    <User size={28} className="text-slate-500" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{selectedVendor.name}</h2>
                  <div className="flex items-center gap-2 mt-1">
                    {getTypeBadge(selectedVendor.type)}
                    <span className="text-xs text-slate-400">•</span>
                    <span className="text-xs text-slate-500">{selectedVendor.phone || 'No phone'}</span>
                  </div>
                </div>
              </div>
              <button onClick={closeDetailModal} className="text-slate-400 hover:text-slate-600">
                <X size={24} />
              </button>
            </div>

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="labour">Labour</TabsTrigger>
                <TabsTrigger value="payments">Payments</TabsTrigger>
                <TabsTrigger value="account">Account</TabsTrigger>
              </TabsList>

              {/* ─── OVERVIEW TAB ────────────────────────────────── */}
              <TabsContent value="overview" className="mt-4 space-y-4">
                {!editMode ? (
                  <>
                    {/* Stats Cards */}
                    {(() => {
                      const stats = getVendorStats(selectedVendor.id);
                      const attStats = getVendorAttendance(selectedVendor.id);
                      const payStats = getVendorPayments(selectedVendor.id);
                      return (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Total Labour</p><p className="text-xl font-bold text-slate-800">{stats.total}</p></CardContent></Card>
                          <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Avg Daily Attendance</p><p className="text-xl font-bold text-blue-600">{attStats.avgDaily}</p></CardContent></Card>
                          <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Total Paid</p><p className="text-xl font-bold text-green-600">₹{payStats.totalPaid.toLocaleString()}</p></CardContent></Card>
                          <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Attendance Rate</p><p className="text-xl font-bold text-amber-600">{attStats.attendanceRate}%</p></CardContent></Card>
                        </div>
                      );
                    })()}

                    {/* Personal Info */}
                    <div className="bg-gradient-to-br from-slate-50 to-slate-100 p-4 rounded-xl border border-slate-200">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <User size={16} className="text-blue-600" />
                        Personal Information
                      </h3>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div><span className="font-medium">Phone:</span> {selectedVendor.phone || '—'}</div>
                        <div><span className="font-medium">Email:</span> {selectedVendor.email || '—'}</div>
                        <div className="col-span-2"><span className="font-medium">Address:</span> {selectedVendor.address || '—'}</div>
                        <div className="col-span-2"><span className="font-medium">Aadhar:</span> {selectedVendor.aadhar || '—'}</div>
                        {selectedVendor.aadharPhoto && (
                          <div className="col-span-2">
                            <span className="font-medium">Aadhar Card:</span>
                            <img
                              src={pb.files.getUrl(selectedVendor, selectedVendor.aadharPhoto)}
                              alt="Aadhar Card"
                              className="mt-2 max-h-40 rounded border shadow-sm"
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Site Breakdown */}
                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Building2 size={16} className="text-purple-600" />
                        Labour Site Breakdown
                      </h3>
                      {getVendorStats(selectedVendor.id).siteBreakdown.length === 0 ? (
                        <p className="text-sm text-slate-400">No labour assigned to any site.</p>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {getVendorStats(selectedVendor.id).siteBreakdown.map(s => (
                            <Badge key={s.siteName} variant="outline" className="bg-white">
                              {s.siteName}: {s.total} ({s.skilled} skilled, {s.unskilled} unskilled)
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="pt-2 flex gap-2">
                      <Button onClick={handleEditFromDetail} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white shadow-md">
                        ✏️ Edit Vendor
                      </Button>
                      <Button variant="outline" onClick={closeDetailModal}>Close</Button>
                    </div>
                  </>
                ) : (
                  // ── Edit Mode ──
                  <form onSubmit={handleSaveFromDetail} className="space-y-4">
                    {formError && (
                      <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">
                        {formError}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                        <Input name="name" value={formData.name} onChange={handleChange} required />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Type *</label>
                        <select name="type" value={formData.type} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400" required>
                          {vendorTypes.map(type => <option key={type} value={type}>{type.replace('_', ' ')}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                        <Input name="phone" value={formData.phone} onChange={handleChange} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                        <Input name="email" type="email" value={formData.email} onChange={handleChange} />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                      <Input name="address" value={formData.address} onChange={handleChange} />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Aadhar</label>
                      <Input name="aadhar" value={formData.aadhar} onChange={handleChange} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Vendor Photo</label>
                        <div className="flex items-center gap-2">
                          <Input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setPhotoFile)} className="flex-1" />
                          {photoFile && <span className="text-xs text-green-600">✓ {photoFile.name}</span>}
                        </div>
                        {selectedVendor.photo && !photoFile && (
                          <span className="text-xs text-slate-400">Current: {selectedVendor.photo}</span>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Aadhar Card Photo</label>
                        <div className="flex items-center gap-2">
                          <Input type="file" accept="image/*" onChange={(e) => handleFileChange(e, setAadharPhotoFile)} className="flex-1" />
                          {aadharPhotoFile && <span className="text-xs text-green-600">✓ {aadharPhotoFile.name}</span>}
                        </div>
                        {selectedVendor.aadharPhoto && !aadharPhotoFile && (
                          <span className="text-xs text-slate-400">Current: {selectedVendor.aadharPhoto}</span>
                        )}
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200">
                      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Banknote size={16} className="text-blue-600" />
                        Bank Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Account Name</label>
                          <Input name="accountName" value={formData.accountName} onChange={handleChange} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Account Number</label>
                          <Input name="accountNumber" value={formData.accountNumber} onChange={handleChange} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Bank Name</label>
                          <Input name="bankName" value={formData.bankName} onChange={handleChange} />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">IFSC Code</label>
                          <Input name="ifscCode" value={formData.ifscCode} onChange={handleChange} />
                        </div>
                      </div>
                      <div className="mt-2">
                        <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                        <Input name="bankDetails" value={formData.bankDetails} onChange={handleChange} />
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t">
                      <Button type="button" variant="outline" onClick={() => { setEditMode(false); setFormError(''); }}>Cancel</Button>
                      <Button type="submit" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white shadow-md" disabled={formLoading}>
                        {formLoading ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </form>
                )}
              </TabsContent>

              {/* ─── LABOUR TAB ───────────────────────────────────── */}
              <TabsContent value="labour" className="mt-4 space-y-4">
                {(() => {
                  const stats = getVendorStats(selectedVendor.id);
                  const attStats = getVendorAttendance(selectedVendor.id);
                  const vendorLabours = labours.filter(l => l.vendor === selectedVendor.id);
                  return (
                    <>
                      {/* Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Total Labour</p><p className="text-xl font-bold text-slate-800">{stats.total}</p></CardContent></Card>
                        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Skilled</p><p className="text-xl font-bold text-emerald-600">{stats.skilled}</p></CardContent></Card>
                        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Unskilled</p><p className="text-xl font-bold text-amber-600">{stats.unskilled}</p></CardContent></Card>
                        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Avg Daily (7 days)</p><p className="text-xl font-bold text-blue-600">{attStats.avgDaily}</p></CardContent></Card>
                      </div>

                      {/* Weekly Attendance Chart */}
                      <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-4 rounded-xl border border-blue-200">
                        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Calendar size={16} className="text-blue-600" />
                          Last 7 Days Attendance
                        </h3>
                        <div className="grid grid-cols-7 gap-2">
                          {Object.keys(attStats.dailyCounts).map((day, idx) => {
                            const date = new Date(day);
                            const count = attStats.dailyCounts[day];
                            const maxPossible = stats.total;
                            const percentage = maxPossible > 0 ? (count / maxPossible) * 100 : 0;
                            return (
                              <div key={idx} className="text-center">
                                <p className="text-xs font-medium text-slate-500">{format(date, 'EEE')}</p>
                                <div className="relative h-12 bg-slate-100 rounded-t-lg overflow-hidden mt-1">
                                  <div 
                                    className="absolute bottom-0 left-0 right-0 bg-blue-500 rounded-t-lg transition-all"
                                    style={{ height: `${Math.min(percentage, 100)}%` }}
                                  />
                                </div>
                                <p className="text-xs font-bold text-slate-700 mt-1">{count}</p>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-slate-500 mt-2">
                          Attendance Rate: <span className="font-bold text-amber-600">{attStats.attendanceRate}%</span>
                          ({attStats.totalPresent} present out of {stats.total * 7} possible)
                        </p>
                      </div>

                      {/* Labour List */}
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <h3 className="text-sm font-semibold text-slate-700 p-4 border-b bg-slate-50 flex items-center gap-2">
                          <Users size={16} className="text-purple-600" />
                          Labour List ({vendorLabours.length})
                        </h3>
                        {vendorLabours.length === 0 ? (
                          <p className="text-sm text-slate-400 p-4">No labour assigned to this vendor.</p>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {vendorLabours.map(l => (
                              <div key={l.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                                <div>
                                  <p className="font-medium text-slate-800">{l.name}</p>
                                  <p className="text-xs text-slate-500">Aadhar: {l.aadhar || 'N/A'}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Badge variant={l.skill === 'skilled' ? 'success' : 'secondary'}>
                                    {l.skill || 'unskilled'}
                                  </Badge>
                                  <span className="text-xs text-slate-400">
                                    {sites.find(s => s.id === l.site)?.name || 'No site'}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              {/* ─── PAYMENTS TAB ────────────────────────────────── */}
              <TabsContent value="payments" className="mt-4">
                {(() => {
                  const payStats = getVendorPayments(selectedVendor.id);
                  return (
                    <>
                      {/* Stats */}
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Total Paid</p><p className="text-xl font-bold text-green-600">₹{payStats.totalPaid.toLocaleString()}</p></CardContent></Card>
                        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Payment Count</p><p className="text-xl font-bold text-slate-800">{payStats.paymentCount}</p></CardContent></Card>
                        <Card><CardContent className="p-3"><p className="text-xs text-slate-500">Last Payment</p><p className="text-sm font-medium text-slate-700">{payStats.lastPayment ? format(new Date(payStats.lastPayment.paymentDate), 'dd MMM yyyy') : '—'}</p></CardContent></Card>
                      </div>

                      {/* Payment List */}
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <h3 className="text-sm font-semibold text-slate-700 p-4 border-b bg-slate-50 flex items-center gap-2">
                          <DollarSign size={16} className="text-green-600" />
                          Payment History
                        </h3>
                        {payStats.payments.length === 0 ? (
                          <p className="text-sm text-slate-400 p-4">No payments recorded.</p>
                        ) : (
                          <div className="divide-y divide-slate-100">
                            {payStats.payments.map(p => (
                              <div key={p.id} className="flex items-center justify-between p-3 hover:bg-slate-50">
                                <div>
                                  <p className="font-medium text-slate-800">₹{p.amount?.toLocaleString()}</p>
                                  <p className="text-xs text-slate-500">{p.paymentMethod?.replace('_', ' ')} • {p.transactionId || 'No ref'}</p>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm text-slate-600">{format(new Date(p.paymentDate), 'dd MMM yyyy')}</p>
                                  <Badge variant="outline" className="text-xs bg-blue-50">{p.paymentType || 'regular'}</Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
              </TabsContent>

              {/* ─── ACCOUNT TAB ──────────────────────────────────── */}
              <TabsContent value="account" className="mt-4">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                    <Banknote size={20} className="text-blue-600" />
                    Bank Account Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="bg-white/70 p-3 rounded-lg">
                      <span className="text-slate-500">Account Name</span>
                      <p className="font-medium text-slate-800">{selectedVendor.accountName || '—'}</p>
                    </div>
                    <div className="bg-white/70 p-3 rounded-lg">
                      <span className="text-slate-500">Account Number</span>
                      <p className="font-medium text-slate-800">{selectedVendor.accountNumber || '—'}</p>
                    </div>
                    <div className="bg-white/70 p-3 rounded-lg">
                      <span className="text-slate-500">Bank Name</span>
                      <p className="font-medium text-slate-800">{selectedVendor.bankName || '—'}</p>
                    </div>
                    <div className="bg-white/70 p-3 rounded-lg">
                      <span className="text-slate-500">IFSC Code</span>
                      <p className="font-medium text-slate-800 uppercase">{selectedVendor.ifscCode || '—'}</p>
                    </div>
                    <div className="col-span-2 bg-white/70 p-3 rounded-lg">
                      <span className="text-slate-500">Notes</span>
                      <p className="font-medium text-slate-800">{selectedVendor.bankDetails || '—'}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Summary */}
                {(() => {
                  const payStats = getVendorPayments(selectedVendor.id);
                  return (
                    <div className="mt-4 bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                      <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                        <TrendingUp size={20} className="text-green-600" />
                        Payment Summary
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-white/70 p-3 rounded-lg text-center">
                          <span className="text-slate-500 text-xs">Total Paid</span>
                          <p className="text-xl font-bold text-green-600">₹{payStats.totalPaid.toLocaleString()}</p>
                        </div>
                        <div className="bg-white/70 p-3 rounded-lg text-center">
                          <span className="text-slate-500 text-xs">Payments</span>
                          <p className="text-xl font-bold text-slate-800">{payStats.paymentCount}</p>
                        </div>
                        <div className="bg-white/70 p-3 rounded-lg text-center">
                          <span className="text-slate-500 text-xs">Last Payment</span>
                          <p className="text-sm font-medium text-slate-700">{payStats.lastPayment ? format(new Date(payStats.lastPayment.paymentDate), 'dd MMM yyyy') : '—'}</p>
                        </div>
                        <div className="bg-white/70 p-3 rounded-lg text-center">
                          <span className="text-slate-500 text-xs">Avg Payment</span>
                          <p className="text-sm font-medium text-slate-700">
                            {payStats.paymentCount > 0 ? `₹${Math.round(payStats.totalPaid / payStats.paymentCount).toLocaleString()}` : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helper ──────────────────────────────────────────────────
function getTypeBadge(type) {
  const colors = {
    meson: 'bg-orange-100 text-orange-700 border-orange-200',
    foreman: 'bg-blue-100 text-blue-700 border-blue-200',
    painter: 'bg-pink-100 text-pink-700 border-pink-200',
    electrician: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    plumber: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    tiles_worker: 'bg-purple-100 text-purple-700 border-purple-200',
    gardener: 'bg-green-100 text-green-700 border-green-200',
    carpenter: 'bg-amber-100 text-amber-700 border-amber-200',
    supply_labour: 'bg-teal-100 text-teal-700 border-teal-200',
    other: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${colors[type] || colors.other}`}>
      {type.replace('_', ' ')}
    </span>
  );
}